import { createSelector, Selector } from '@reduxjs/toolkit'
import {
  ClearingHouseDetail,
  ClearingHouseInteraction,
  ClearingHouseType,
  EntityStatus,
  EntityType,
  ProductSector,
  SponsorType,
  TradingEntityClearing,
  TradingEntityClearingDetail,
  TradingEntityClearingType
} from 'api'
import { GlobalState } from 'store'
import {
  createAllEntitiesSelector,
  createAllEntityUpdatesSelector,
  createEntityGetterSelector,
  createSortedEntityIdsSelector
} from './entitySelectors'
import { isOwnTradingEntityGetterSelector } from './adminSelectors'
import { FICC_CLEARING_HOUSE_ID } from 'model'
import { ParametricSelector } from 'utils/redux'

export const clearingHouseDetailsSelector: Selector<GlobalState, ClearingHouseDetail[]> = createSelector(
  [createAllEntityUpdatesSelector(EntityType.CLEARING_HOUSE)],
  clearingHouseUpdates => {
    return clearingHouseUpdates
      .filter(update => update.entity.id !== FICC_CLEARING_HOUSE_ID)
      .map(update => {
        const clearingHouse = update.entity
        const availableEvents = update.availableEvents
        return { clearingHouse, availableEvents }
      })
  }
)

export const enabledClearingHouseDetailsSelector: Selector<
  GlobalState,
  ClearingHouseDetail[]
> = createSelector(clearingHouseDetailsSelector, clearingHouseDetails =>
  clearingHouseDetails.filter(clearingHouseDetail => clearingHouseDetail.clearingHouse.status === EntityStatus.ENABLED)
)

export const tradingEntityClearingsSelector: Selector<GlobalState, TradingEntityClearing[]> = createSelector(
  [createAllEntitiesSelector(EntityType.TRADING_ENTITY_CLEARING), isOwnTradingEntityGetterSelector],
  (tradingEntityClearings, isOwnTradingEntityGetter) => {
    return tradingEntityClearings
      .filter(tradingEntityClearing => tradingEntityClearing.clearingHouseId !== FICC_CLEARING_HOUSE_ID)
      .filter(tradingEntityClearing => isOwnTradingEntityGetter(tradingEntityClearing.tradingEntityId))
  }
)

export const tradingEntityClearingByIdGetterSelector: Selector<
  GlobalState,
  (entityId: string) => TradingEntityClearing | undefined
> = createSelector(tradingEntityClearingsSelector, tradingEntityClearings => (entityId: string) =>
  tradingEntityClearings.find(tradingEntityClearing => tradingEntityClearing.id === entityId)
)

export const tradingEntityClearingsByTradingEntityIdSelector: Selector<
  GlobalState,
  (tradingEntityId: string) => TradingEntityClearing[]
> = createSelector(tradingEntityClearingsSelector, tradingEntityClearings => {
  return (tradingEntityId: string) =>
    tradingEntityClearings.filter(tradingEntityClearing => tradingEntityClearing.tradingEntityId === tradingEntityId)
})

export const tradingEntityClearingDetailsGetterSelector: Selector<
  GlobalState,
  (tradingEntityClearings: TradingEntityClearing[]) => TradingEntityClearingDetail[]
> = createSelector(
  [
    createEntityGetterSelector(EntityType.CLEARING_HOUSE),
    createEntityGetterSelector(EntityType.SPONSOR),
    createEntityGetterSelector(EntityType.PRODUCT_SECTOR)
  ],
  (clearingHouseGetter, sponsorGetter, productSectorGetter) => {
    return tradingEntityClearings =>
      tradingEntityClearings
        .filter(tradingEntityClearing => tradingEntityClearing.clearingHouseId !== FICC_CLEARING_HOUSE_ID)
        .map(tradingEntityClearing => {
          const clearingHouse = clearingHouseGetter(tradingEntityClearing.clearingHouseId)
          const sponsor = tradingEntityClearing.sponsorId !== null ? sponsorGetter(tradingEntityClearing.sponsorId) : null
          const productSectors =
            tradingEntityClearing.productSectorIds && tradingEntityClearing.productSectorIds.length > 0
              ? tradingEntityClearing.productSectorIds.reduce((productSectorsAcc: ProductSector[], productSectorId: string) => {
                  const productSector = productSectorGetter(productSectorId)
                  if (productSector) {
                    productSectorsAcc.push(productSector)
                  }
                  return productSectorsAcc
                }, [])
              : null

          return {
            tradingEntityClearing,
            clearingHouse,
            sponsor,
            productSectors
          }
        })
  }
)

export const getTradingEntityClearingDetailsByTradingEntityIdGetterSelector: Selector<
  GlobalState,
  (tradingEntityId: string, tradingEntityClearingType: TradingEntityClearingType) => TradingEntityClearingDetail[]
> = createSelector(
  [tradingEntityClearingsByTradingEntityIdSelector, tradingEntityClearingDetailsGetterSelector],
  (getEntityClearings, getClearingDetails) => (tradingEntityId: string, tradingEntityClearingType: TradingEntityClearingType) =>
    getClearingDetails(getEntityClearings(tradingEntityId).filter(entityClearing => entityClearing.type === tradingEntityClearingType))
)

export const tradingEntityClearingDetailsSelector: Selector<
  GlobalState,
  TradingEntityClearingDetail[]
> = createSelector(
  tradingEntityClearingsSelector,
  tradingEntityClearingDetailsGetterSelector,
  (tradingEntityClearings, getTradingEntityClearingDetails) => getTradingEntityClearingDetails(tradingEntityClearings)
)

export function compareClearingHouseInteraction(entry1: ClearingHouseInteraction, entry2: ClearingHouseInteraction): number {
  const time1 = entry1.createdInstant
  const time2 = entry2.createdInstant

  return time1 < time2 ? 1 : time1 > time2 ? -1 : 0
}

export function createClearingHouseInteractionsForDealSelector<TProps>(dealIdSelector: ParametricSelector<{}, TProps, string>) {
  return createSelector(
    [dealIdSelector, createAllEntitiesSelector(EntityType.CLEARING_HOUSE_INTERACTION)],
    (dealId, clearingHouseInteractions) => {
      return clearingHouseInteractions
        .filter(clearingHouseInteraction => clearingHouseInteraction.dealId === dealId)
        .sort(compareClearingHouseInteraction)
    }
  )
}

export function createClearingSponsorIdsSelector<TProps>(
  fundIdSelector: ParametricSelector<{}, TProps, string>,
  clearingHouseIdSelector: ParametricSelector<{}, TProps, string | null>
) {
  return createSelector(
    [
      fundIdSelector,
      clearingHouseIdSelector,
      createAllEntitiesSelector(EntityType.TRADING_ENTITY_CLEARING),
      createSortedEntityIdsSelector(EntityType.SPONSOR)
    ],
    (fundId, clearingHouseId, tradingEntityClearings, allSortedSponsorIds) => {
      const qualifyingSponsorIds = tradingEntityClearings
        .filter(
          tradingEntityClearing =>
            tradingEntityClearing.status === EntityStatus.ENABLED &&
            tradingEntityClearing.type === TradingEntityClearingType.SPONSORED &&
            tradingEntityClearing.tradingEntityId === fundId &&
            tradingEntityClearing.clearingHouseId === clearingHouseId
        )
        .map(tradingEntityClearing => tradingEntityClearing.sponsorId)

      return allSortedSponsorIds.filter(id => qualifyingSponsorIds.includes(id))
    }
  )
}

export function createCounterpartyClearingSponsorIdsSelector<TProps>(
  fundIdSelector: ParametricSelector<{}, TProps, string>,
  clearingHouseIdSelector: ParametricSelector<{}, TProps, string | null | undefined>,
  productSectorIdSelector: ParametricSelector<{}, TProps, string | null | undefined>
) {
  return createSelector(
    [
      fundIdSelector,
      clearingHouseIdSelector,
      productSectorIdSelector,
      createAllEntitiesSelector(EntityType.TRADING_ENTITY_CLEARING),
      createSortedEntityIdsSelector(EntityType.SPONSOR)
    ],
    (fundId, clearingHouseId, productSectorId, tradingEntityClearings, allSortedSponsorIds) => {
      if (!clearingHouseId || !productSectorId) {
        return []
      }

      const qualifyingSponsorIds = tradingEntityClearings
        .filter(
          tradingEntityClearing =>
            tradingEntityClearing.status === EntityStatus.ENABLED &&
            tradingEntityClearing.type === TradingEntityClearingType.SPONSORED &&
            tradingEntityClearing.tradingEntityId === fundId &&
            tradingEntityClearing.clearingHouseId === clearingHouseId &&
            tradingEntityClearing.productSectorIds.includes(productSectorId)
        )
        .map(tradingEntityClearing => tradingEntityClearing.sponsorId)

      return allSortedSponsorIds.filter(id => qualifyingSponsorIds.includes(id))
    }
  )
}

export function createHasDirectClearingRelationshipSelector<TProps>(
  tradingEntityIdSelector: ParametricSelector<{}, TProps, string>,
  clearingHouseIdSelector: ParametricSelector<{}, TProps, string | null | undefined>,
  productSectorIdSelector: ParametricSelector<{}, TProps, string | null | undefined>
): ParametricSelector<GlobalState, TProps, boolean> {
  return createSelector(
    tradingEntityIdSelector,
    clearingHouseIdSelector,
    productSectorIdSelector,
    createAllEntitiesSelector(EntityType.TRADING_ENTITY_CLEARING),
    (tradingEntityId, clearingHouseId, productSectorId, tradingEntityClearings) => {
      if (!clearingHouseId || !productSectorId) {
        return false
      }

      return (
        tradingEntityClearings.filter(
          tradingEntityClearing =>
            tradingEntityClearing.status === EntityStatus.ENABLED &&
            tradingEntityClearing.type === TradingEntityClearingType.DIRECT &&
            tradingEntityClearing.tradingEntityId === tradingEntityId &&
            tradingEntityClearing.clearingHouseId === clearingHouseId &&
            tradingEntityClearing.productSectorIds.includes(productSectorId)
        ).length > 0
      )
    }
  )
}

const ClearingHouseLookup = {
  [ClearingHouseType.LCH]: 'LCH Ltd',
  [ClearingHouseType.LCHSA]: 'LCH SA',
  [ClearingHouseType.FICC]: 'Fixed Income Clearing Corporation'
}

export const clearingHouseIdByTypeGetterSelector: Selector<GlobalState, (name: ClearingHouseType) => string | null> = createSelector(
  [createAllEntitiesSelector(EntityType.CLEARING_HOUSE)],
  allClearingHouses => name =>
    allClearingHouses.find(clearingHouse => clearingHouse.publishedName === ClearingHouseLookup[name])?.id ?? null
)

export const sponsorIdByTypeGetterSelector: Selector<GlobalState, (name: SponsorType) => string | null> = createSelector(
  [createAllEntitiesSelector(EntityType.SPONSOR)],
  allSponsors => name => allSponsors.find(sponsor => sponsor.publishedName === name)?.id ?? null
)
